/**
 * Constants — Derived from content.json.
 *
 * This module reads the content file and exports values that
 * multiple components need: navigation, search index, site title, etc.
 *
 * The search index performs DEEP content indexing: it walks
 * every section, sub-section, text block, callout, support card,
 * reading list, and code block to build a comprehensive, flat,
 * searchable corpus. If a word exists anywhere in the document,
 * the search will find it.
 *
 * ZERO content is defined here. Everything comes from content.json.
 */

import type { NavSection, SearchResult, SiteContent, ContentBlock, Segment } from './types';
import contentRaw from '../content.json';

const content = contentRaw as unknown as SiteContent;

/* ── Navigation ── */
export const NAVIGATION: NavSection[] = content.navigation;

/* ── Site metadata ── */
export const SITE_TITLE: string = content.meta.siteTitle;
export const SITE_AUTHOR: string = content.footer.author;

/* ── Search UI strings ── */
export const SEARCH_UI = content.search;

/* ── Header UI strings ── */
export const HEADER_UI = content.header;

/**
 * Extract plain text from a segments array.
 * Used to build searchable strings from rich inline content.
 */
function segmentsToText(segments: Segment[]): string {
  return segments.map((s) => s.text).join('');
}

/**
 * Build the search index from navigation, actions, AND deep content.
 * Pure function, safe for SSR.
 *
 * Index depth:
 *   - Search actions (pinned)
 *   - Navigation items + children
 *   - Section titles
 *   - Sub-section titles
 *   - Text block content (from segments)
 *   - Callout titles + content
 *   - Support card labels + values
 *   - Reading list item titles
 *   - Code block content
 */
export function buildSearchIndex(): SearchResult[] {
  const items: SearchResult[] = [];
  const seen = new Set<string>(); // Deduplicate by title+href

  function add(title: string, category: string, href: string, type: SearchResult['type']) {
    const key = `${title}::${href}`;
    if (seen.has(key)) return;
    seen.add(key);
    // Skip very short fragments
    if (title.trim().length < 3) return;
    items.push({ title, category, href, type });
  }

  // ── 1. Pinned actions ──
  content.searchActions.forEach((action) => {
    items.push({
      title: action.title,
      category: action.category,
      href: action.href,
      type: 'Action',
      shortcut: action.shortcut || undefined,
    });
  });

  // ── 2. Navigation items ──
  NAVIGATION.forEach((section) => {
    section.links.forEach((link) => {
      add(link.title, section.title, link.href, 'Navigation');
      link.children?.forEach((child) => {
        add(child.title, link.title, child.href, 'Navigation');
      });
    });
  });

  // ── 3. Deep content walk ──
  function walkBlocks(blocks: ContentBlock[], sectionTitle: string, sectionHref: string) {
    blocks.forEach((block) => {
      switch (block.type) {
        case 'text': {
          const text = segmentsToText(block.segments);
          if (text.length > 15) {
            const preview = text.substring(0, 80) + (text.length > 80 ? '…' : '');
            add(preview, sectionTitle, sectionHref, 'Content');
          }
          // Also index individual terms as standalone entries
          block.segments.forEach((seg) => {
            if (seg.term) {
              add(seg.text, sectionTitle, sectionHref, 'Content');
            }
          });
          break;
        }

        case 'callout': {
          add(block.title, sectionTitle, sectionHref, 'Content');
          const calloutText = segmentsToText(block.segments);
          if (calloutText.length > 15) {
            const preview = calloutText.substring(0, 80) + (calloutText.length > 80 ? '…' : '');
            add(preview, block.title, sectionHref, 'Content');
          }
          break;
        }

        case 'sub-section': {
          const subHref = `#${block.id}`;
          add(block.title, sectionTitle, subHref, 'Content');
          // Recurse into sub-section blocks
          walkBlocks(block.blocks, block.title, subHref);
          break;
        }

        case 'reading-list': {
          block.items.forEach((item) => {
            add(item.title, sectionTitle, sectionHref, 'Content');
          });
          break;
        }

        case 'support': {
          block.items.forEach((item) => {
            add(`${item.label}: ${item.value}`, sectionTitle, sectionHref, 'Content');
          });
          break;
        }

        case 'code': {
          if (block.code.length > 10) {
            const preview = block.code.substring(0, 60) + (block.code.length > 60 ? '…' : '');
            add(preview, block.title || sectionTitle, sectionHref, 'Content');
          }
          break;
        }

        case 'card': {
          walkBlocks(block.blocks, sectionTitle, sectionHref);
          break;
        }
      }
    });
  }

  content.sections.forEach((section) => {
    const href = `#${section.id}`;
    add(section.title, section.tagline || 'Document', href, 'Content');
    walkBlocks(section.blocks, section.title, href);
  });

  return items;
}
